import { useCallback } from 'react';
import { ERPPostingStatus, HighnoteTransactionEvent } from 'API';
import { useUserRole } from 'app/hooks/useUserRole';
import { useApprovalLevels } from 'app/hooks/transactions/useApprovalLevels';
import { useCanApproveGroupStep } from 'app/hooks/transactions/useCanApproveGroupStep';
import { useCanReviewGroupTransaction } from 'app/hooks/transactions/useCanReviewGroupTransaction';
import { useEditLockingEnabled } from 'app/hooks/transactions/useEditLockingEnabled';
import { useAppSelector } from 'app/store';
import { selectTransactionsSlice } from 'app/pages/store/transactionsSlice';
import { selectUserProfile } from 'app/store/userProfileSlice';
import { canEditTransaction } from 'util/transactionEditLock';
import { AccountType } from 'util/workflowStatus';

/**
 * Whether the current user may edit a transaction's coding, notes, receipts and
 * fuel capture. Per transaction rather than per role, so it returns a callback
 * the same way useCanApproveGroupStep does.
 *
 * The account type is a parameter rather than derived here: every call site
 * already knows which coding system it is rendering, and deriving it again would
 * be a second answer that could disagree with the one driving the form.
 */
export const useCanEditTransaction = (accountType: AccountType) => {
  const { isCardholder, canMarkTransactionReviewed, canApproveTransactionFinal, canPostTransactionToErp } =
    useUserRole();
  const approvalLevels = useApprovalLevels(accountType);
  const strictMode = useEditLockingEnabled();
  const canReviewGroupTransaction = useCanReviewGroupTransaction();
  const canApproveGroupStep = useCanApproveGroupStep();
  // Destructured defensively: this hook is called from every transaction
  // surface, and a slice that has not hydrated yet would otherwise throw and
  // take the whole page down rather than degrade to "not the owner".
  const { profile } = useAppSelector(selectUserProfile) ?? {};
  const { postingTransactionIds } = useAppSelector(selectTransactionsSlice) ?? {};

  return useCallback(
    (transaction: HighnoteTransactionEvent | null | undefined): boolean => {
      // A post the client has fired but the row has not caught up with yet —
      // the same signal the ERP table cells already lock on.
      const isTransactionPosting =
        !!transaction &&
        ((postingTransactionIds ?? []).includes(transaction.id) ||
          transaction.erpPostingStatus === ERPPostingStatus.PENDING ||
          transaction.erpPostingStatus === ERPPostingStatus.PROCESSING);

      return canEditTransaction(transaction, {
        strictMode,
        accountType,
        approvalLevels,
        currentUserId: profile?.id ?? null,
        isCardholder,
        canMarkTransactionReviewed,
        canApproveTransactionFinal,
        canPostTransactionToErp,
        canReviewGroupTransaction,
        canApproveGroupStep,
        isTransactionPosting,
      });
    },
    [
      accountType,
      approvalLevels,
      canApproveGroupStep,
      canApproveTransactionFinal,
      canMarkTransactionReviewed,
      canPostTransactionToErp,
      canReviewGroupTransaction,
      isCardholder,
      postingTransactionIds,
      profile?.id,
      strictMode,
    ]
  );
};
